import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { STELE_PAGE_KIND, STELE_CLIENT, isTombstone, isStelePage, spaceCoordinate } from '@/lib/stele';

/** Fetch all pages that belong to a given space */
export function useSpacePages(pubkey: string | undefined, spaceSlug: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent[]>({
    queryKey: ['nostr', 'stele', 'pages', pubkey ?? '', spaceSlug ?? ''],
    queryFn: async (c) => {
      if (!pubkey || !spaceSlug) return [];
      const coord = spaceCoordinate(pubkey, spaceSlug);
      const events = await nostr.query(
        [{ kinds: [STELE_PAGE_KIND], authors: [pubkey], '#a': [coord], '#client': [STELE_CLIENT] }],
        { signal: c.signal },
      );
      // Client-side validation: relays may not perfectly filter multi-tag queries
      return events.filter(e => isStelePage(e) && !isTombstone(e.tags));
    },
    enabled: !!pubkey && !!spaceSlug,
  });
}

/** Fetch a single page by pubkey + page slug */
export function usePage(pubkey: string | undefined, pageSlug: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent | null>({
    queryKey: ['nostr', 'stele', 'page', pubkey ?? '', pageSlug ?? ''],
    queryFn: async (c) => {
      if (!pubkey || !pageSlug) return null;
      const dTag = `stele:page:${pageSlug}`;
      const events = await nostr.query(
        [{ kinds: [STELE_PAGE_KIND], authors: [pubkey], '#d': [dTag], '#client': [STELE_CLIENT], limit: 1 }],
        { signal: c.signal },
      );
      const event = events[0];
      if (!event || !isStelePage(event) || isTombstone(event.tags)) return null;
      return event;
    },
    enabled: !!pubkey && !!pageSlug,
  });
}
